/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import {
    format,
    getPayloadObject,
    getTelemetryAttributes,
    ITelemetryContent,
    onTelemetryClick
} from '@msdyn365-commerce-modules/utilities';
import { getUrlSync, IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { observer } from 'mobx-react';
import * as React from 'react';

export interface ICartIconData {
    cart?: ICartState;
}

export interface ICartIconComponentProps extends IComponentProps<ICartIconData> {
    className?: string;
    cartLabel: string;
    cartQtyLabel: string /* Upgraded to 10.0.16 */;
    telemetryContent?: ITelemetryContent;
}

export interface ICartIconComponent extends IComponent<ICartIconComponentProps> {}

const CartIconComponentActions = {};

/**
 *
 * CartIcon component
 * @extends {React.FC<ICartIconComponentProps>}
 */
const CartIcon: React.FC<ICartIconComponentProps> = observer((props: ICartIconComponentProps) => {
    const {
        cartLabel,
        data: { cart }
    } = props;
    const cartLink = getUrlSync('cart', props.context.actionContext);

    /* VSI Customization - Start - 22/10/2020*/
    /* '()' small brackets for cart qunatity are removed */
    const cartItem = cart ? (cart.hasInvoiceLine ? 0 : `${cart.totalItemsInCart}`) : 0; /* Upgraded to 10.0.16 */
    /* VSI Customization - End */

    const label = format(cartLabel, cartItem) || `Shopping bag, ${cartItem} items`;
    // Construct telemetry attribute to render
    const payLoad = getPayloadObject('click', props.telemetryContent!, 'cart-icon', '');
    const attributes = getTelemetryAttributes(props.telemetryContent!, payLoad);
    return (
        <a
            title={label}
            className={Number(cartItem) > 0 ? 'ms-header__icon-cart fill' : 'ms-header__icon-cart'}
            href={cartLink}
            aria-label={label}
            {...attributes}
            onClick={onTelemetryClick(props.telemetryContent!, payLoad, 'cart-icon')}
        >
            {/* VSI Customization - Start - 22/10/2020 */}
            {/* Custom css classes added for cart qunatity shown inside bag icon */}
            <span className='ms-header__icon-cart__count'>{Number(cartItem) > 0 ? cartItem : ''}</span>
            <span className='sr-only'>Cart</span>
            {/* VSI Customization - End */}
        </a>
    );
});

export const CartIconComponent: React.FunctionComponent<ICartIconComponentProps> = msdyn365Commerce.createComponentOverride<
    // @ts-ignore
    ICartIconComponent
>('CartIcon', { component: CartIcon, ...CartIconComponentActions });

export default CartIconComponent;
